import { ArrayUtil, Collection, ObjectUtil, Stack } from "@mcleod/core";
import { DataSource } from "..";
import { Event, EventListener } from "../events/Event";
import { EventListenerList, getOwnerFunction } from "../events/EventListenerList";
import {
    DesignableObjectPropDefinition, DesignableObjectProps, DesignableObjectPropsCollection
} from "./DesignableObjectProps";
import { ListenerListDef } from "./ListenerListDef";
import { ListenerListDefUtil } from "./ListenerListDefUtil";
import { PropType } from "./PropType";
import { DesignableObjectTempState } from "./DesignableObjectTempState";
import { DesignableObjectLogManager } from "../logging/DesignableObjectLogManager";
import { DesignableObjectCreationMethod } from "./DesignableObjectCreationMethod";

const log = DesignableObjectLogManager.getLogger("components.base.DesignableObject");

export abstract class DesignableObject {
    private _owner: any;
    private __interactionEnabled: boolean = true;
    protected _id: string;
    private _creationMethod: DesignableObjectCreationMethod;
    private _baseVersionProps: any;
    private tempStateStack: Stack<DesignableObjectTempState>;
    private _alternateDesignerTarget: DesignableObject;
    private propertyDefaultValueOverrides: Map<string, any>;

    constructor(creationMethod: DesignableObjectCreationMethod = DesignableObjectCreationMethod.NORMAL) {
        //Set the _creationMethod flag first, before any other props are set.
        //The way we provide this value (including the direct object access) is unfortunate, but it seems wrong to add
        //_creationMethod as a DesignableObjectProp. Really it should be a different arg to the constructor, but then
        //every descendent of DesignableObject would need a new constructor.
        this._creationMethod = creationMethod;
    }

    public get id(): string {
        return this._id;
    }

    public set id(value: string) {
        this._id = value;
    }

    get owner(): any {
        return this._owner;
    }

    set owner(value: any) {
        this._owner = value;
    }

    public abstract getDesigner(): any;
    public abstract setDesigner(designer: any): void;

    public getPropertyDefinitions(): DesignableObjectPropsCollection {
        throw new Error("Component type " + this.constructor.name + " must implement getPropertyDefinitions().");
    }

    public setProps(props: Partial<DesignableObjectProps>) {
        if (props != null) {
            const compProps = this.getPropertyDefinitions();
            if ((props as any)._designer != null)
                this.setDesigner((props as any)._designer);
            for (const key in props)
                if (key !== "_designer") {
                    this.setPropUsingPropsCollection(props[key], key, compProps);
                }
        }
    }

    public isCloning(): boolean {
        return this._creationMethod === DesignableObjectCreationMethod.CLONING;
    }

    public isDeserializing(): boolean {
        return this._creationMethod === DesignableObjectCreationMethod.DESERIALIZING;
    }

    public clearCreationMethod() {
        delete this._creationMethod;
    }

    /**
     * This will set properties on this object and return an object containing the original values of those properties.
     * See ObjectUtil.replaceObjectProps for more detail.
     * @param props The properties that you wish to apply to this object
     * @returns The original values of the properties that were set
     */
    public replaceProps(props: Partial<DesignableObjectProps>): Partial<DesignableObjectProps> {
        return ObjectUtil.replaceObjectProps(this, props);
    }

    public async replacePropsDuring(props: Partial<DesignableObjectProps>, promiseFunction: Promise<any>, restorePropsOnError: boolean = true): Promise<any> {
        const originalProps = this.replaceProps(props);
        return promiseFunction.then(promiseResponse => {
            this.replaceProps(originalProps);
            return Promise.resolve(promiseResponse);
        }).catch(promiseError => {
            if (restorePropsOnError)
                this.replaceProps(originalProps);
            return Promise.reject(promiseError);
        });
    }

    public setPropUsingPropsCollection(value: any, key: string, compProps: Collection<DesignableObjectPropDefinition>) {
        const compProp = compProps[key];
        this.setProp(value, key, compProp);
    }

    public setProp(value: any, key: string, compProp: DesignableObjectPropDefinition) {
        const designer = this.getDesigner();
        if (compProp == null || compProp.type !== PropType.event || designer != null) {
            this[key] = value;
        }
        else if (!(this instanceof DataSource && designer != null)) {
            const addListenerMethod = compProp.addListenerMethod;
            if (addListenerMethod != null) {
                let eventFunction = value;
                if (this.getDesigner() == null && typeof eventFunction === "string") {
                    eventFunction = getOwnerFunction(this, key, eventFunction);
                }
                if (typeof eventFunction === "function") {
                    this[addListenerMethod](eventFunction);
                }
            }
        }
    }

    protected createEventListenerListIfNeeded(which: ListenerListDef) {
        if (this.getListeners(which.listName) == null)
            this._setListeners(which.listName, new EventListenerList(this, which));
    }

    protected addEventListener(which: ListenerListDef, value: EventListener): DesignableObject {
        this.createEventListenerListIfNeeded(which);
        this.getListeners(which.listName).add(value);
        return this;
    }

    protected insertEventListener(which: ListenerListDef, value: EventListener, index: number): DesignableObject {
        this.createEventListenerListIfNeeded(which);
        this.getListeners(which.listName).insert(value, index);
        return this;
    }

    protected removeEventListener(which: string | ListenerListDef, value: EventListener): DesignableObject {
        const list = this.getListeners(which);
        if (list != null) {
            for (let i = list.listeners.length - 1; i >= 0; i--) {
                const item = list.listeners[i];
                if (item === value || item?.wrapped === value)
                    list.listeners.splice(i, 1);
            }
            if (list.isEmpty())
                this._setListeners(which, undefined);
        }
        return this;
    }

    protected removeAllEventListeners(which: string | ListenerListDef): DesignableObject {
        const list = this.getListeners(which);
        if (list != null) {
            list.removeAll();
            this._setListeners(which, undefined);
        }
        return this;
    }

    hasListener(which: ListenerListDef, listener: EventListener): boolean {
        const list = this.getListeners(which.listName);
        return list != null && list.hasListener(listener);
    }

    hasListeners(which: ListenerListDef): boolean {
        const list = this.getListeners(which.listName);
        return list != null && !list.isEmpty();
    }

    fireListeners<EventType extends Event = Event>(name: string | ListenerListDef, event: Event | (() => Event)): EventType {
        if (typeof name === "object")
            name = name.listName;
        const list: EventListenerList = this[name];
        if (list != null && !list.isEmpty())
            return list.fireListeners<EventType>(event);
    }

    getListeners(name: string | ListenerListDef): EventListenerList {
        const n = ListenerListDefUtil.getListName(name);
        return this[n] as EventListenerList;
    }

    getFirstListener(name: string | ListenerListDef): EventListener {
        const n = ListenerListDefUtil.getListName(name);
        const list = this[n] as EventListenerList;
        return (ArrayUtil.isEmptyArray(list.listeners) !== true) ? list.listeners[0] : null;
    }

    getLastListener(name: string | ListenerListDef): EventListener {
        const n = ListenerListDefUtil.getListName(name);
        const list = this[n] as EventListenerList;
        return (ArrayUtil.isEmptyArray(list.listeners) !== true) ? list.listeners[list.listeners.length - 1] : null;
    }

    private _setListeners(name: string | ListenerListDef, value: EventListenerList) {
        const n = ListenerListDefUtil.getListName(name);
        this[n] = value;
    }

    public getEventTarget(): HTMLElement {
        return null;
    }

    get _interactionEnabled(): boolean {
        return this.__interactionEnabled;
    }

    set _interactionEnabled(value: boolean) {
        this.__interactionEnabled = value;
    }

    getListenerDefs(): Collection<ListenerListDef> {
        return {};
    }

    shareListenersFrom(otherDO: DesignableObject, listeners?: ListenerListDef[]) {
        const thisDefs = this.getListenerDefs();
        if (thisDefs == null)
            return;
        for (const listenerDef of Object.values(thisDefs)) {
            if (listeners != null && listeners.includes(listenerDef) !== true)
                continue;
            if (otherDO.hasListeners(listenerDef))
                this._setListeners(listenerDef, otherDO.getListeners(listenerDef).copyFor(this));
        }
    }

    public get baseVersionProps(): any {
        return this._baseVersionProps;
    }

    public set baseVersionProps(value: any) {
        this._baseVersionProps = value;
        this.syncBaseVersionProps();
    }

    protected syncBaseVersionProps() {
        if (ObjectUtil.isEmptyObject(this._baseVersionProps))
            this._baseVersionProps = null;
    }

    public popTempState(): DesignableObjectTempState {
        return this.tempStateStack?.pop();
    }

    /**
     * This method will update the designable object with property values from the most recently-added state object.
     * @param forcePropNames A list of property names.  Properties included in the list will be applied as part of the
     * reversion, even if the property's value has changed since the temporary state object was created.
     * @returns void
     */
    public revertTempState(forcePropNames?: string[]): DesignableObjectTempState {
        const previousState = this.tempStateStack?.pop();
        if (this.tempStateStack != null && this.tempStateStack.isEmpty() === true)
            this.tempStateStack = null;
        this.applyPropsFromState(previousState, forcePropNames);
        return previousState;
    }

    /**
     * This method will update the designable object with property values from the oldest state object.
     * @param forcePropNames A list of property names.  Properties included in the list will be applied as part of the
     * reversion, even if the property's value has changed since the temporary state object was created.
     * @returns void
     */
    public revertToOriginalState(forcePropNames?: string[]): DesignableObjectTempState {
        const originalState = this.tempStateStack?.getOldest();
        this.tempStateStack = null;
        this.applyPropsFromState(originalState, forcePropNames);
        return originalState;
    }

    private applyPropsFromState(tempState: DesignableObjectTempState, forcePropNames?: string[]) {
        if (tempState == null)
            return;
        //Do not apply properties that have changed since the temporary state object was created, unless the calling
        //method has indicated the property application should take place no matter what (by the property name's
        //inclusion in the forcePropNames array)
        const propsToApply = { ...tempState.originalProps };
        for (const propName of Object.keys(tempState.originalProps)) {
            const currentPropValue = this[propName];
            const appliedPropValue = tempState.appliedProps[propName];
            if (currentPropValue !== appliedPropValue && forcePropNames?.includes(propName) !== true) {
                const message = "Not applying value from temporary state object for property %s; the value has" +
                    " changed since temporary state object was created";
                log.debug(this, message, propName)
                delete propsToApply[propName];
            }
        }
        this.setProps(propsToApply);
    }

    public discardAllStates() {
        this.tempStateStack = null;
    }

    public applyTempState(newPropValues: Partial<DesignableObjectProps>, addlPropsToInclude?: string[]):
        DesignableObjectTempState {
        this.initializeTempStateStack();
        const stateObject = this.createStateObject(newPropValues, addlPropsToInclude);
        this.tempStateStack.push(stateObject);
        this.setProps(newPropValues);
        return stateObject;
    }

    public createStateObject(newPropValues: Partial<DesignableObjectProps>, addlPropsToInclude?: string[]):
        DesignableObjectTempState {
        const currentPropValues = {};
        const appliedPropValues = { ...newPropValues };
        this.addToPropsObject(Object.keys(newPropValues), currentPropValues);
        if (ArrayUtil.isEmptyArray(addlPropsToInclude) === false) {
            this.addToPropsObject(addlPropsToInclude, currentPropValues);
            //For properties that are being included in the state's original values, but aren't being changed, store
            //the current property value as the applied value.  This lets us know if the property has changed later on.
            this.addToPropsObject(addlPropsToInclude, appliedPropValues);
        }
        return new DesignableObjectTempState(currentPropValues, appliedPropValues);
    }

    private initializeTempStateStack() {
        if (this.tempStateStack == null)
            this.tempStateStack = new Stack<DesignableObjectTempState>();
    }

    private addToPropsObject(propNames: string[], object: any) {
        for (const propName of propNames) {
            object[propName] = this[propName];
        }
    }

    /**
     * Determines if the DesignableObject is selectable in the UI Designer.
     * @returns a boolean indicating if the DesignableObject is selectable
     */
    public get selectableInDesigner(): boolean {
        return this._alternateDesignerTarget != null;
    }

    /**
     * Allows a DesignableObject, when selected in the UI Designer, to cause a different element to be selected.
     * @returns the DesignableObject to select
     */
    public get alternateDesignerTarget(): DesignableObject {
        return this._alternateDesignerTarget;
    }

    public set alternateDesignerTarget(value: DesignableObject) {
        this._alternateDesignerTarget = value;
    }

    /**
     * Used by the designer to determine which props should be included during serialization.  Most DesignableObject
     * subclasses will have the same default value for a property regardless of the other properties in the
     * DesignableObject, but some may choose to change the default value for property X when property Y changes.
     * For example, when a Component is databound, the default value for some properties will come from the bound field
     * (the field metadata).
     * @param prop
     * @returns
     */
    getPropertyDefaultValue(prop: DesignableObjectPropDefinition): any {
        let result = this.getPropertyDefaultValueOverride(prop.name);
        if (result == null)
            result = this.determinePropertyDefaultValue(prop);
        if (result == null)
            result = prop.defaultValue;
        return result;
    }

    /**
     * This method should contain logic related to individual property default values.  Override this method in
     * subclasses (which allows for not duplicating the override logic in each subclass).  The overridden
     * method in the subclass should call super.determinePropertyDefaultValue(prop) at the end of its implementation
     * (when the subclass doesn't specify the default for the property).
     * @param prop
     * @returns
     */
    protected determinePropertyDefaultValue(prop: DesignableObjectPropDefinition): any {
        return null;
    }

    private getPropertyDefaultValueOverride(propertyName: string): any {
        return this.propertyDefaultValueOverrides?.get(propertyName);
    }

    addPropertyDefaultValueOverrides(props: Partial<DesignableObjectProps>) {
        Object.entries(props).forEach((singleOverride: [key: string, value: any]) => {
            this.addPropertyDefaultValueOverride(singleOverride[0], singleOverride[1]);
        });
    }

    addPropertyDefaultValueOverride(propertyName: string, overrideValue: any) {
        if (this.propertyDefaultValueOverrides == null)
            this.propertyDefaultValueOverrides = new Map<string, any>();
        this.propertyDefaultValueOverrides.set(propertyName, overrideValue);
    }
    public get logPrefix(): string {
        return this.id != null ? (this.id + ":") : "";
    }
}
